import React, { useContext, useState, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  styled,
} from '@mui/material';
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import CloseIcon from '@mui/icons-material/Close';

import GeneralContext from '../GeneralContext/GeneralContext';

export default function PrefacturaAlert({
  defaultOpen = true,
  onClose,
}) {
  /* ───── integración con el Context global ───── */
  const ctx = useContext(GeneralContext);
  const ctxAvailable =
    ctx &&
    Object.prototype.hasOwnProperty.call(ctx, 'prefacturaBannerOpen') &&
    Object.prototype.hasOwnProperty.call(ctx, 'closePrefacturaBanner');

  /* Si NO estamos dentro del context global, usamos estado local */
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = ctxAvailable ? ctx.prefacturaBannerOpen : localOpen;

  /* ───── cálculo de meses dinámicos ───── */
  // Este hook se ejecuta siempre, antes de cualquier return.
  const { currentPrefactura, monitoredData, discountData } = useMemo(() => {
    const monthNames = [
      'ENERO',
      'FEBRERO',
      'MARZO',
      'ABRIL',
      'MAYO',
      'JUNIO',
      'JULIO',
      'AGOSTO',
      'SEPTIEMBRE',
      'OCTUBRE',
      'NOVIEMBRE',
      'DICIEMBRE',
    ];

    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth(); // 0 = enero, …, 11 = diciembre

    // 1) Contar días hábiles desde el 1 del mes hasta hoy (inclusive)
    let businessDaysCount = 0;
    const iter = new Date(year, monthIndex, 1);
    while (iter <= today) {
      const dayOfWeek = iter.getDay(); // 0 = domingo, 1 = lunes, …, 6 = sábado
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        businessDaysCount++;
      }
      iter.setDate(iter.getDate() + 1);
    }
    // 2) Decidir cuántos meses retroceder para "monitoredData":
    //    - Si ya pasaron más de 5 días hábiles, usamos mesIndex - 1
    //    - Si no, usamos mesIndex - 2
    const offsetForMonitored = businessDaysCount > 5 ? 1 : 2;
    const monitoredIndex = (monthIndex - offsetForMonitored + 12) % 12;

    // 3) "Descuentos" siempre es un mes antes que "monitoredData"
    const discountIndex = (monitoredIndex - 1 + 12) % 12;

    return {
      currentPrefactura: monthNames[monthIndex],
      monitoredData: monthNames[monitoredIndex],
      discountData: monthNames[discountIndex],
    };
  }, []); // se calcula solo al montar

  /* ───── callback de cierre ───── */
  // También debe colocarse antes del if(!open)
  const handleClose = useCallback(() => {
    if (ctxAvailable) {
      ctx.closePrefacturaBanner?.();
    } else {
      setLocalOpen(false);
    }
    onClose?.();
  }, [ctxAvailable, ctx, onClose]);

  /* ───── early return si está cerrado ───── */
  if (!open) return null;

  /* ───── estilos reutilizables ───── */
  const BorderCell = styled(TableCell)(({ theme }) => ({
    border: '1px solid rgba(0,0,0,0.25)',
    padding: theme.spacing(0.5, 2),
    whiteSpace: 'nowrap',
  }));

  /* ───── render ───── */
  return (
    <Box
      sx={{
        bgcolor: '#FFB39D',
        borderRadius: 2,
        pl: 3,
        pr: 1,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
        <NotificationImportantOutlinedIcon fontSize="large" />
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ lineHeight: 1.1, textAlign: 'right' }}
        >
          INFORMACIÓN IMPORTANTE
          <br />
          SOBRE FACTURACIÓN
        </Typography>
      </Stack>

      <Box sx={{ ml: 2, flexGrow: 1, overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <TableBody>
            <TableRow>
              <BorderCell align="center">Mes&nbsp;actual</BorderCell>
              <BorderCell align="center">Datos&nbsp;monitoreados&nbsp;en:</BorderCell>
              <BorderCell align="center">Descuentos&nbsp;de:</BorderCell>
            </TableRow>
            <TableRow>
              <BorderCell align="center" sx={{ fontWeight: 700 }}>
                {currentPrefactura}
              </BorderCell>
              <BorderCell align="center" sx={{ fontWeight: 700 }}>
                {monitoredData}
              </BorderCell>
              <BorderCell align="center" sx={{ fontWeight: 700 }}>
                {discountData}
              </BorderCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <IconButton size="small" sx={{ ml: 1 }} onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
